import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Row, Col, CloseButton, Spinner } from 'reactstrap';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntityProfileDetails } from 'app/entities/user-profile/user-profile.reducer';
import axios from 'axios';

export const IntrovideoUpdate = props => {
  const dispatch = useAppDispatch();
  const userProfileId = props.userId;
  const isNew = userProfileId === null;
  const userProfileEntity = useAppSelector(state => state.userProfile.entity);
  const loading = useAppSelector(state => state.userProfile.loading);
  const updating = useAppSelector(state => state.userProfile.updating);
  const [error, setError] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [introductionVideoLink, setIntroductionVideoLink] = useState('');
  const [apiError, setApiError] = useState('');
  const [mediaPreview, setMediaPreview] = useState('');
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [videoFileName, setVideoFileName] = useState(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existingVideo, setExistingVideo] = useState(null);

  const baseImageUrl = '/image';
  const account = useAppSelector(state => state.authentication.account);
  const userid = account.id;

  const handleClose = useCallback(() => {
    props.handleSubmit();
  }, [props.handleSubmit]);

  // Load user profile data once
  useEffect(() => {
    if (!isNew && userProfileId && !hasFetchedData) {
      dispatch(getEntityProfileDetails(userProfileId));
      setHasFetchedData(true);
    }
  }, [isNew, userProfileId, dispatch, hasFetchedData]);

  const getVideoUrl = async (fileId: string) => {
    if (!fileId) return null;
    const requestUrl = `/downloadIntroductionVideo?fileId=${fileId}`;
    try {
      setUploading(true);
      const response = await axios.get(requestUrl, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error fetching video blob:', error);
      return null;
    } finally {
      setUploading(false);
    }
  };
  // Populate form when entity is fetched
  useEffect(() => {
    if (!isNew && userProfileEntity && userProfileEntity[0]?.user?.id === userProfileId) {
      const entity = userProfileEntity[0];
      if (entity.introductionVideoFileId) {
        setExistingVideo(entity.introductionVideoFileId);
        const videoID = entity.introductionVideoFileId?.replace(/\.[^/.]+$/, '');

        getVideoUrl(videoID).then(url => {
          if (url) {
            setMediaPreview(url);
            setVideoFileName(videoID);
          }
        });
      }
      if (entity.videoDescription) {
        setVideoDescription(entity.videoDescription);
      }
      if (entity.videoTitle) {
        setVideoTitle(entity.videoTitle);
      }
      if (entity.introductionVideoLink) {
        setIntroductionVideoLink(entity.introductionVideoLink);
      }
    }
  }, [isNew, userProfileEntity, userProfileId]);

  // Reset when unmounting
  useEffect(() => {
    return () => {
      setHasFetchedData(false);
    };
  }, [userProfileId]);

  const handleMediaFileInputChange = event => {
    const selectedFile = event.target.files[0];
    setError(''); // Clear previous errors
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please upload only video files');
        return;
      }
      // Validate file size
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError('File size should be less than 50MB');
        return;
      }
      const previewUrl = URL.createObjectURL(selectedFile);
      setMediaPreview(previewUrl);
      setUploadedMedia(selectedFile);
      setError('');
    }
  };

  const handleRemoveMedia = () => {
    setMediaPreview('');
    setUploadedMedia(null);
    setVideoFileName(null);
    setExistingVideo(null);
  };

  // const handleDescriptionChange = value => {
  //   console.log(value, 'description');
  //   if (value.split(' ').length <= 500) {
  //     setVideoDescription(value);
  //     setError('');
  //   } else {
  //     setError('Only 500 words are allowed in video description');
  //   }
  // };

  // const saveEntity = async values => {
  //   try {
  //     let introductionVideoFileId = videoFileName;

  //     const formData = new FormData();
  //     if (uploadedMedia) {
  //       formData.append('image', uploadedMedia);
  //     }

  //     // use latest state values
  //     formData.append('title', values.videoTitle);
  //     formData.append('description', values.videoDescription);
  //     formData.append('introductionVideoLink', values.introductionVideoLink);
  //     const response = await axios.post('/api/uploadIntroductionVideo?type=video', formData, {
  //       headers: { 'Content-Type': 'multipart/form-data' },
  //     });

  //     introductionVideoFileId = response.data;

  //     handleClose();
  //   } catch (error) {
  //     setApiError('Error saving introduction video. Please try again.');
  //     console.error('Error saving introduction video:', error);
  //   }
  // };

  const saveEntity = async values => {
    if (!values.introductionVideoLink && !uploadedMedia && !videoFileName) {
      setApiError('Please provide either an personal video link or upload a video.');
      return;
    }
    try {
      setUploading(true);
      // const existingFileID = userProfileEntity?.[0]?.introductionVideoFileId;
      let introductionVideoFileId = videoFileName;
      const formData = new FormData();
      if (uploadedMedia) {
        formData.append('image', uploadedMedia);
      }
      if (!!existingVideo) {
        formData.append('existingFileId', existingVideo);
      }

      formData.append('title', values.videoTitle);
      formData.append('description', values.videoDescription);
      formData.append('introductionVideoLink', values.introductionVideoLink);

      const response = await axios.post('/api/uploadIntroductionVideo?type=video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      introductionVideoFileId = response.data;
      handleClose();
    } catch (error) {
      setApiError('Error saving introduction video. Please try again.');
      console.error('Error saving introduction video:', error);
    } finally {
      setUploading(false);
    }
  };

  // Memoize defaults so they don’t reset typing
  const defaultValues = useMemo(
    () =>
      isNew ? { videoTitle: '', videoDescription: '', introductionVideoLink: '' } : { videoTitle, videoDescription, introductionVideoLink },
    [isNew, videoTitle, videoDescription, introductionVideoLink]
  );

  return (
    <div className="intro-video-card mb-5">
      <div className="edit-create-pages">
        <Row className="justify-content-start">
          <Col md="8">
            <h2 id="fivePlusApp.userProfile.introVideo.createOrEditLabel" data-cy="introVideoCreateUpdateHeading">
              <label>{isNew ? 'Add' : 'Edit'} Personal Introduction Video</label>
            </h2>
          </Col>
        </Row>
        <Row className="justify-content-start">
          <Col md="8">
            {uploading && (
              <div
                className="overlay-loader d-flex flex-column align-items-center justify-content-center"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 9999,
                }}
              >
                <Spinner className="body-loader-icon" />
              </div>
            )}
            {loading ? (
              <Spinner className="body-loader-icon" />
            ) : (
              <ValidatedForm defaultValues={defaultValues} onSubmit={saveEntity}>
                <label htmlFor="user-profile-videoTitle">
                  {translate('fivePlusApp.userProfile.videoTitle')} <span className="text-danger">*</span>
                </label>
                <ValidatedField
                  id="user-profile-videoTitle"
                  name="videoTitle"
                  data-cy="videoTitle"
                  type="text"
                  validate={{ required: 'Video title is required' }}
                  valid={false}
                />
                <label htmlFor="user-profile-videoTitle">
                  {translate('fivePlusApp.userProfile.videoDescription')} <span className="text-danger">*</span>
                </label>
                <ValidatedField
                  id="user-profile-videoDescription"
                  name="videoDescription"
                  data-cy="videoDescription"
                  type="textarea"
                  placeholder="Enter video description (max 50 words)"
                  validate={{ required: 'Video description is required' }}
                  valid={false}
                />
                <label htmlFor="user-profile-introductionVideoLink" className="form-label">
                  Personal Video Link
                </label>
                <small className="text-muted d-block mb-1">Please enter a link from a public account.</small>
                <ValidatedField
                  // label="Interview Link"
                  id="user-profile-introductionVideoLink"
                  name="introductionVideoLink"
                  data-cy="introductionVideoLink"
                  type="text"
                  placeholder="Enter personal video link (e.g., https://meet.google.com/abc-xyz)"
                  validate={{
                    pattern: {
                      value: /https?:\/\/(?:www\.)?[a-zA-Z0-9\-_.]+(?:\.[a-zA-Z]{2,})(?:\/[^\s]*)?/g,
                      message: 'Please enter a valid URL',
                    },
                  }}
                  valid={false}

                  // validate={{ required: 'Video description is required' }}
                />
                {error && <div className="text-danger">{error}</div>}
                <ValidatedField
                  id="media-file"
                  name="mediaFile"
                  data-cy="mediaFile"
                  type="file"
                  label="Upload Video"
                  className="col-12 col-md-6 col-lg-4 position-relative secondary-field"
                  onChange={handleMediaFileInputChange}
                  accept="video/mp4,video/mp3,video/quicktime"
                  valid={false}
                  disabled={videoFileName !== null ? true : false}
                />
                {mediaPreview && (
                  <Col md={12}>
                    <div className="remove-media-btn">
                      <CloseButton onClick={handleRemoveMedia} />
                      <video
                        key={mediaPreview} // ensures refresh when URL changes
                        controls
                        className="mb-4 p-0"
                        style={{ maxWidth: '100%' }}
                        src={mediaPreview}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </Col>
                )}
                {apiError && <div className="text-danger">{apiError}</div>}
                &nbsp;
                <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                  <FontAwesomeIcon icon="save" />
                  &nbsp;
                  <Translate contentKey="entity.action.save">Submit</Translate>
                </Button>
              </ValidatedForm>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default IntrovideoUpdate;
